// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

interface NavGroupProps {
  label: string;
  icon: LucideIcon;
  items: {
    path: string;
    label: string;
    icon: LucideIcon;
  }[];
  isExpanded: boolean;
  onToggle: () => void;
}

export default function NavGroup({ label, icon: GroupIcon, items, isExpanded, onToggle }: NavGroupProps) {
  const { pathname } = useLocation();
  const isActive = (path: string) => pathname === path;
  const hasActiveChild = items.some((item) => isActive(item.path));

  return (
    <div>
      <button
        onClick={onToggle}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
          hasActiveChild && !isExpanded
            ? 'text-brand-700 bg-brand-50'
            : 'text-slate-600 hover:bg-slate-100'
        }`}
        aria-expanded={isExpanded}
      >
        <GroupIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">{label}</span>
        <ChevronRight
          className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-0.5 border-l border-slate-200 pl-3">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                isActive(item.path)
                  ? 'bg-brand-100 text-brand-800'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
